import { randomUuid } from "@models/utils/uuid";
import { Ioc } from "@core/ioc/ioc";
import { aliasesToDto, readAliases, type PartAlias } from "@models/data/aliasData";
import {
  assetObjectKeyForFormat,
  type AssetDto,
} from "@models/data/assetDto";
import {
  nextEntityDisplayOrder,
  type EntityData,
  type EntityTransform,
} from "@models/data/entityData";
import type { EntitySnapshotDto } from "@models/data/entitySnapshotDto";
import type { EntitySettingsDto } from "@models/data/entitySettingsDto";
import { toNameFloatMap, type NameFloatMap } from "@models/data/nameFloatMap";
import { type PartDto, partMaterialIds } from "@models/data/partDto";
import type { MaterialDto } from "@models/data/materialDto";
import { cloneMaterialRows, remapMaterialIds } from "@models/materials/materialCopy";
import { mapEntities } from "@models/data/mapEntities";
import type { ParsedModel, ParsedPart } from "@models/importer/IImporterModel";
import type { ISelectionModel } from "@models/scene/ISelectionModel";
import { SELECTION_MODEL } from "@models/scene/ISelectionModel";
import type { IUndoRedo } from "@models/project/IUndoRedo";
import { UNDO_REDO } from "@models/project/IUndoRedo";
import type { IStorageModel } from "@models/storage/IStorageModel";
import type { IPreviewCaptureHost } from "@models/project/ProjectModelThumbnail";
import type { GltfAssetCache } from "@runtime/assets/gltfAssetCache";
import { GLTF_ASSET_CACHE } from "@runtime/assets/gltfAssetCache";
import type { ProjectModel } from "./ProjectModel";
import { isStructuralWriteRefusedWhileDelegated } from "./presenterDelegationGuard";

/**
 * `ProjectModelUpload` — partial of {@link ProjectModel} owning model import
 * ("Add Model"). Port of `Models/ProjectModel/ProjectModel_UploadAsset.cs`
 * (the write/spawn pipeline), reduced to the browser-doable glTF path.
 *
 * The {@link WebImporterModel} has already parsed the file into a
 * {@link ParsedModel} (parts + snapshots + placement scale). This partial is
 * the inverse of the load path (`ensureSceneEntitiesAsync`): register the new
 * asset's parts into the catalog, build the entity in memory, inject the parsed
 * parsed glTF into the render cache so it appears instantly, then persist its
 * GLB or Unity-compatible `.gltfzip` package to S3 + Dynamo (Phase 3) and
 * publish a realtime `create_entity` event (Phase 5).
 *
 * Deviations from Unity (documented per web port rules):
 * - **D1/D2** textures stay embedded in the uploaded glTF package; no
 *   MaterialData / TextureData rows are written (embedded glTF-PBR renders
 *   natively).
 * - ~~**D3** `DisplayOrder` is `existing entity count` (append-at-end)…~~
 *   **RETIRED 2026-08-18.** The count collides with a live order in any scene a
 *   model has been deleted from, and the resulting tie seated the model
 *   differently here than in Unity — which renumbers the root segment of every
 *   part path after it. Every creation path now mints
 *   `nextEntityDisplayOrder` = Unity's `max DisplayOrder + 1`.
 * - ~~**D7** the entity root snapshot carries a `(1,1,-1)` scale…~~ **RETIRED
 *   2026-08-10 (task 0004).** It was never a deviation, it was a bug: the port's
 *   Unity↔three map had determinant +1 (a 180° Y rotation where a reflection is
 *   required), and this `-1` existed only to cancel an equal-and-opposite mirror
 *   in the renderer. Both halves are gone; the root snapshot this file writes is
 *   now fully identity, matching Unity. Rows written before that date still
 *   carry the `-1` and are repaired by `scripts/repair-0004-root-scale.js`.
 *
 * Failure handling (task 0014): a failed import compensates in the CLOUD, not
 * just in memory — `rollbackImportAsync` issues real deletes for every write
 * that committed, in reverse order, mirroring the intent of Unity's
 * `IRollbackStack` in `ProjectDataModel.CreateEntityAsync`. An ABORT
 * compensates exactly like a failure: the web has no resume mechanism —
 * nothing ever re-attaches to half-written rows, and without the scene-row
 * `AssetIDs` link the reload path renders none of them — so leaving the rows
 * behind is indistinguishable from orphaning them. The unwind itself always
 * runs UNSIGNALLED (the caller's controller may already be aborted, and a
 * cleanup that cancels itself is worse than none) and never masks the
 * original error.
 */
export class ProjectModelUpload {
  /**
   * Upload a model as project-level console furniture without spawning a scene
   * entity. The asset/part rows use the normal Campfire library contract, then
   * `ConsoleAssetID` and project `AssetIDs` are updated atomically.
   */
  async importProjectConsoleAsync(
    this: ProjectModel,
    parsed: ParsedModel,
    signal?: AbortSignal,
  ): Promise<{ assetId: string }> {
    const project = this.activeProject;
    if (!project) {
      throw new Error("Cannot import a console model without an active project.");
    }

    const assetId = randomUuid();
    const objectKey = assetObjectKeyForFormat(assetId, parsed.format);
    const parts: PartDto[] = parsed.parts.map((part) => partDtoOf(part, assetId));
    registerParts(this, assetId, parts);
    injectIntoCache(assetId, parsed);

    const context = storageContext(this);
    if (context) {
      const committed = emptyProjectAssetCommitLog();
      try {
        await context.storage.putAssetObject(
          context.customerId,
          objectKey,
          parsed.blob,
          signal,
        );
        committed.s3Object = true;
        await context.storage.createAsset(
          context.customerId,
          assetDtoOf(assetId, parsed),
          signal,
        );
        committed.assetRow = true;
        await uploadAssetPreview(this, context, assetId, signal);
        // `createParts` is a chunked batch; "started" means some rows may
        // already require compensation even if the promise rejects.
        committed.partRows = true;
        await context.storage.createParts(
          context.customerId,
          assetId,
          parts,
          signal,
        );
        if (this.activeProject?.id !== project.id) {
          throw new Error("The active project changed before the console upload completed.");
        }
        await this.setProjectConsoleAssetAsync(assetId, signal);
      } catch (err) {
        await rollbackProjectAssetImportAsync(
          this,
          context,
          assetId,
          objectKey,
          parts,
          committed,
        );
        throw err;
      }
    } else {
      await this.setProjectConsoleAssetAsync(assetId, signal);
    }

    return { assetId };
  }

  /**
   * Import a parsed model into the active scene. Mirrors the step order of
   * `UploadAssetAsync`: mint ids → build asset/part/snapshot data → inject the
   * GLB for instant render → in-memory spawn → persist → publish.
   * Returns the new `{ assetId, entityId }`.
   */
  async importModelAsync(
    this: ProjectModel,
    parsed: ParsedModel,
    signal?: AbortSignal,
  ): Promise<{ assetId: string; entityId: string }> {
    const scene = this.activeScene;
    if (!this.activeProject || !scene) {
      throw new Error("Cannot import a model without an active project + scene.");
    }
    const state = internals(this);
    const sceneId = scene.id;

    const assetId = randomUuid();
    const entityId = randomUuid();

    // Build the part catalog + snapshot rows from the parsed model.
    const parts: PartDto[] = parsed.parts.map((p) => partDtoOf(p, assetId));
    const snapshots: EntitySnapshotDto[] = parsed.parts.map((p) =>
      snapshotDtoOf(p, entityId),
    );
    const displayOrder = nextEntityDisplayOrder(this.getEntities(sceneId));
    const settings: EntitySettingsDto = { EntityID: entityId, DisplayOrder: displayOrder };

    // Register parts into the catalog (mirrors the load path in
    // `ensureSceneEntitiesAsync`) so `mapEntities` can resolve assetId +
    // hierarchy for the new snapshots.
    registerParts(this, assetId, parts);

    // Build the in-memory `EntityData` via the shared load-path mapper, then
    // append it to the scene's entity list.
    const [entity] = mapEntities({
      sceneId,
      snapshots,
      settings: [settings],
      partIdToAssetId: state.m_partIdToAssetId,
      partHierarchyById: state.m_partHierarchyById,
      partTransformById: state.m_partTransformById,
    });
    if (!entity) throw new Error("Import produced no entity data.");
    appendEntity(this, sceneId, entity);

    // Inject the parsed GLB so the renderer places it with zero S3 round-trip.
    injectIntoCache(assetId, parsed);

    // Add the asset to the scene (drives reload's parts catalog + smooth
    // transition support) and notify renderers / hierarchy / inspector — the
    // model is now visible before any cloud write completes.
    addAssetToScene(this, sceneId, assetId);
    this.onEntityChangesApplied.emit({ sceneId });

    // Persist to S3 + Dynamo in Unity's `CreateEntityAsync` order (the order
    // itself is contract — the asset-preview leg deliberately sits between
    // `createAsset` and `createParts`). Each resolved await marks its step in
    // `committed`, so a failure can compensate exactly the writes that
    // actually landed — in the cloud, not just in memory (task 0014).
    const context = storageContext(this);
    if (context) {
      const objectKey = assetObjectKeyForFormat(assetId, parsed.format);
      const committed = emptyCommitLog();
      try {
        await context.storage.putAssetObject(context.customerId, objectKey, parsed.blob, signal);
        committed.s3Object = true;
        await context.storage.createAsset(context.customerId, assetDtoOf(assetId, parsed), signal);
        committed.assetRow = true;
        // Unity uploads the asset-library thumbnail here — between
        // `CreateAsset` and `CreateParts` (`ProjectDataModel.CreateEntityAsync`).
        // Non-fatal + best-effort, so no step marker of its own: the rollback
        // deletes the preview whenever the asset row committed (the S3 delete
        // tolerates a missing key).
        await uploadAssetPreview(this, context, assetId, signal);
        // The two BATCH steps are marked BEFORE their await, unlike the
        // single-item writes around them: `createParts`/`createEntitySnapshots`
        // are sequential 25-row BatchWriteItem loops, so an abort or failure
        // mid-stream leaves the earlier chunks durably written while the call
        // rejects. Gating the sweep on the fully-resolved flag orphaned
        // exactly those chunks (an 890-snapshot import cancelled late = ~875
        // stranded rows, re-fetched and warn-dropped by every later scene
        // load). Sweeping a started-but-unconfirmed step is free — the
        // deletes are per-key idempotent and tolerate missing rows.
        committed.partRows = true;
        await context.storage.createParts(context.customerId, assetId, parts, signal);
        committed.snapshotRows = true;
        await context.storage.createEntitySnapshots(
          context.customerId,
          context.projectId,
          sceneId,
          snapshots,
          signal,
        );
        await context.storage.createEntitySettings(
          context.customerId,
          context.projectId,
          sceneId,
          settings,
          signal,
        );
        committed.settingsRow = true;
        // Retried once before giving up: it is the cheapest write of the
        // sequence and the ONLY one whose loss makes an otherwise-complete
        // import invisible — the reload path builds its parts catalog solely
        // from the scene row's `AssetIDs`.
        try {
          await context.storage.updateSceneAssetIDs(
            context.customerId,
            context.projectId,
            sceneId,
            state.m_assetIdsByScene.get(sceneId) ?? [assetId],
            signal,
          );
        } catch (firstErr) {
          // Never retry an ABORT: the signal stays aborted, so the second
          // attempt would reject before any I/O — a wasted round trip and a
          // misleading "retrying" warn for a write the user walked away from.
          if (isAbortRejection(firstErr, signal)) throw firstErr;
          console.warn(
            "[ProjectModel] updateSceneAssetIDs failed — retrying once:",
            firstErr,
          );
          await context.storage.updateSceneAssetIDs(
            context.customerId,
            context.projectId,
            sceneId,
            state.m_assetIdsByScene.get(sceneId) ?? [assetId],
            signal,
          );
        }
        await syncProjectAssetIds(this, signal);
      } catch (err) {
        await rollbackImportAsync(this, context, {
          sceneId,
          entityId,
          assetId,
          objectKey,
          parts,
          snapshots,
          committed,
        });
        throw err;
      }
    }

    // Publish a realtime `create_entity` event so other connected clients
    // reconcile the new entity (they re-fetch parts + asset from cloud).
    publishCreateEntity(this, sceneId, assetId, entityId);

    // Record undo: the initial spawn already ran, so the command's first
    // `redo()` (fired by `record`) is a no-op; later undo deletes the entity
    // and redo re-persists it against the still-uploaded asset.
    recordImportUndo(this, sceneId, assetId, entityId, parts, snapshots, settings);

    // Select the imported model so the inspector + hierarchy focus it (visual
    // confirmation; Unity selects the spawned object post-upload).
    if (Ioc.isRegistered<ISelectionModel>(SELECTION_MODEL)) {
      const rootSnapshotId = parsed.parts.find((p) => p.isRoot)?.snapshotId;
      if (rootSnapshotId) Ioc.resolve<ISelectionModel>(SELECTION_MODEL).set([rootSnapshotId]);
    }

    // Local edit → refresh the scene card (debounced).
    this.scheduleActiveSceneThumbnail();
    return { assetId, entityId };
  }

  /**
   * Duplicate an entity in-place. Port of Unity's duplication chain
   * (`DuplicationModel.DuplicateGameObjects` → `SceneModel.SpawnAsset` →
   * `UploadAssetAsync` with `existingAssetInfo`): the asset row, part rows,
   * and S3 object are REUSED; only new entity snapshot + settings rows are
   * written. New snapshot ids are minted per part (Unity
   * `CreateEntitySnapshotDataAsync`); `PartID`s, transforms, names, and
   * `InitialPos`/`InitialRot` carry over (Unity `AssetFactory.CopyTransform` +
   * `SetInitialPosition(original.GetInitialPosition())`), so the duplicate
   * lands exactly on top of the source. Publishes `create_entity`, records an
   * undoable command (undo deletes the duplicate only — the shared asset stays
   * linked to the scene), and selects the new root.
   *
   * Material rows are cloned for the duplicate, matching Unity's
   * `AssetFactory.CopyMaterials` / `ProjectDuplicationModel.CopyMaterials` —
   * without it, editing the copy's material would edit the original's, since
   * a material row is customer-scoped and the two would name one row.
   *
   * Deviations (documented per web port rules):
   * - Source rows are re-fetched from Dynamo so attributes the web doesn't
   *   model survive verbatim — duplication therefore requires the online
   *   storage context (throws offline).
   */
  async duplicateEntityAsync(
    this: ProjectModel,
    sceneId: string,
    entityId: string,
    signal?: AbortSignal,
    opts?: { readonly targetSceneId?: string },
  ): Promise<{ entityId: string } | null> {
    // AI-1426 safety net — see `presenterDelegationGuard`.
    if (isStructuralWriteRefusedWhileDelegated("duplicateEntity")) return null;
    const source = this.getEntityById(sceneId, entityId);
    if (!source) return null;
    const assetId = source.assetId;
    if (!assetId) {
      throw new Error("Only asset-backed entities can be duplicated (media tiles have their own path).");
    }
    const context = storageContext(this);
    if (!context) {
      throw new Error("Duplicate requires the online storage context.");
    }
    const state = internals(this);
    // Paste reads the SOURCE scene's rows and writes them into the active one
    // (Unity `DuplicationModel.PasteGameObjects` spawns into
    // `m_projectModel.ActiveScene`, which is why it pins the buffer's scene
    // persistent). Duplicate leaves both the same.
    const targetSceneId = opts?.targetSceneId ?? sceneId;

    // Clone the source rows at DTO level so unmapped attributes carry over.
    const [allSnaps, allSettings] = await Promise.all([
      context.storage.getEntitySnapshots(context.customerId, context.projectId, sceneId, undefined, signal),
      context.storage.getEntitySettings(context.customerId, context.projectId, sceneId, signal),
    ]);
    const sourceSnaps = allSnaps.filter((s) => s.EntityID === entityId);
    if (sourceSnaps.length === 0) {
      throw new Error("Duplicate failed: source entity has no snapshot rows in the cloud.");
    }
    // The duplicate gets its own material rows (Unity `CopyMaterials`) —
    // before any local mutation, so a failure leaves nothing to roll back.
    const materialClone = await cloneMaterialRows(
      context.storage,
      context.customerId,
      sourceSnaps.flatMap((s) => s.MaterialIDs ?? []),
      signal,
    );
    for (const copy of materialClone.copies) state.m_materialsById.set(copy.ID, copy);

    const newEntityId = randomUuid();
    const snapshots: EntitySnapshotDto[] = sourceSnaps.map((s) => ({
      ...s,
      EntityID: newEntityId,
      SnapshotID: randomUuid(),
      MaterialIDs: remapMaterialIds(s.MaterialIDs ?? [], materialClone.idMap),
    }));
    const sourceSettings = allSettings.find((s) => s.EntityID === entityId);
    const settings: EntitySettingsDto = {
      ...sourceSettings,
      EntityID: newEntityId,
      DisplayOrder: nextEntityDisplayOrder(this.getEntities(targetSceneId)),
    };

    // In-memory spawn (parts are already in the catalog — the source entity
    // is loaded — but guard the edge where they aren't).
    if (!state.m_partsLoadedAssets.has(assetId)) {
      const cloudParts = await context.storage.getParts(context.customerId, assetId, signal);
      registerParts(this, assetId, cloudParts);
    }
    const [entity] = mapEntities({
      sceneId: targetSceneId,
      snapshots,
      settings: [settings],
      partIdToAssetId: state.m_partIdToAssetId,
      partHierarchyById: state.m_partHierarchyById,
      partTransformById: state.m_partTransformById,
    });
    if (!entity) throw new Error("Duplicate produced no entity data.");
    // The source's materials are normally already cached (it is in a loaded
    // scene), so this is a no-op — but a paste into another scene, or a source
    // whose own batch failed, would otherwise inherit the flat-colour fallback.
    await internals(this).ensureMaterialsLoadedAsync(
      snapshots.flatMap((s) => s.MaterialIDs ?? []),
      signal,
    );
    appendEntity(this, targetSceneId, entity);
    addAssetToScene(this, targetSceneId, assetId); // idempotent
    this.onEntityChangesApplied.emit({ sceneId: targetSceneId });

    // Persist the new rows (Unity `CreateEntityAsync` with
    // `assetAlreadyExists == true` skips asset/part/S3 writes entirely).
    try {
      await context.storage.createEntitySnapshots(context.customerId, context.projectId, targetSceneId, snapshots, signal);
      await context.storage.createEntitySettings(context.customerId, context.projectId, targetSceneId, settings, signal);
      await context.storage.updateSceneAssetIDs(
        context.customerId,
        context.projectId,
        targetSceneId,
        state.m_assetIdsByScene.get(targetSceneId) ?? [assetId],
        signal,
      );
      await syncProjectAssetIds(this, signal);
    } catch (err) {
      removeEntity(this, targetSceneId, newEntityId);
      this.onEntityChangesApplied.emit({ sceneId: targetSceneId });
      throw err;
    }

    publishCreateEntity(this, targetSceneId, assetId, newEntityId);
    recordDuplicateUndo(this, targetSceneId, assetId, newEntityId, snapshots, settings);

    // Select the duplicate (Unity `DuplicationModel.OnLoaded` selects the new
    // object).
    if (Ioc.isRegistered<ISelectionModel>(SELECTION_MODEL)) {
      const rootSnapshotId = snapshots.find((s) => s.IsRoot === true)?.SnapshotID;
      if (rootSnapshotId) Ioc.resolve<ISelectionModel>(SELECTION_MODEL).set([rootSnapshotId]);
    }

    // Local edit → refresh the scene card (debounced).
    this.scheduleActiveSceneThumbnail();
    return { entityId: newEntityId };
  }

  /**
   * Place an existing library asset into the active scene — port of the
   * `AssetLibraryViewModel.SelectAsset` → `BaseSceneModel.SpawnAsset` →
   * `AssetFactory.CreateAsync(assetInfo, …)` →
   * `UploadAssetAsync(existingAssetInfo)` chain. "Presence of
   * existingAssetInfo forces this method NOT to upload the package"
   * (`ProjectModel_UploadAsset.cs`), so the asset row, part rows, and the
   * S3 binary are all REUSED: only new snapshot + settings rows are written,
   * the scene's `AssetIDs` gains the id, and `create_entity` (tc 13) fires.
   *
   * Unlike {@link duplicateEntityAsync} (which clones another entity's
   * snapshot rows) the snapshots here are synthesized from the asset's PART
   * catalog, exactly like Unity's `GetPartData(assetID, transforms)` match
   * by `HierachyID`: one snapshot per part, carrying the part's authored TRS
   * as both the live and initial pose, its visibility, name, merged flag,
   * and its library `MaterialIDs` — remapped through this placement's own
   * freshly minted material rows.
   *
   * ⚠ TRS and visibility are the two fields Unity does NOT take verbatim, and
   * both exceptions are load-bearing — see the policy block inside, which
   * ports `ModifyAssetUsingPartDataAsync`'s two root branches and
   * `AssetFactory.FixInvisibleParts`.
   *
   * That mint is the whole reason a project's material edits stay inside the
   * project: material rows are customer-scoped, so sharing the library part's
   * ids meant an edit here rewrote the row the library entry AND every other
   * project's placement read. Port of `CreateMaterialDataAsync`
   * (`ProjectModel_UploadAsset.cs:597`), which mints on every spawn including
   * the `existingAssetInfo` branch — see `models/materials/materialCopy.ts`.
   */
  async placeLibraryAssetAsync(
    this: ProjectModel,
    assetId: string,
    signal?: AbortSignal,
  ): Promise<{ entityId: string } | null> {
    const sceneId = this.activeSceneId;
    if (!sceneId) throw new Error("Place from library requires an active scene.");
    const context = storageContext(this);
    if (!context) {
      throw new Error("Place from library requires the online storage context.");
    }
    const state = internals(this);

    const parts = await context.storage.getParts(context.customerId, assetId, signal);
    if (parts.length === 0) {
      throw new Error(`Library asset ${assetId} has no part rows.`);
    }
    if (!state.m_partsLoadedAssets.has(assetId)) registerParts(this, assetId, parts);

    // Mint this placement's OWN material rows before anything local is
    // mutated (port of `CreateMaterialDataAsync`, see `materialCopy.ts`). The
    // library's part rows keep their ids, so the library entry — and every
    // other project that placed this asset — is unreachable from an edit made
    // here. A throw at this point has changed nothing and needs no rollback.
    const materialClone = await cloneMaterialRows(
      context.storage,
      context.customerId,
      parts.flatMap((part) => partMaterialIds(part)),
      signal,
    );
    for (const copy of materialClone.copies) state.m_materialsById.set(copy.ID, copy);

    // The library version this placement is being made from, so the
    // update prompt has a watermark to compare against. Best-effort: without
    // it the placement simply reads as "unknown" and is back-filled on the
    // next scene load.
    let librarySyncedVersion: number | undefined;
    try {
      const assetRow = await context.storage.getAssetById(context.customerId, assetId, signal);
      const version = assetRow?.AssetVersion;
      if (typeof version === "number" && Number.isFinite(version) && version > 0) {
        librarySyncedVersion = version;
        state.m_assetVersionById.set(assetId, version);
      }
    } catch (err) {
      console.warn(`[ProjectModel] place from library: getAssetById failed (${assetId}):`, err);
    }

    // Unity's root part is the one whose HierachyID addresses the wrapper
    // root ("0,"); everything else is a child part.
    //
    // ⚠ `PartData.visibility` is assigned by exactly ONE upstream path — Save
    // to Library (`ProjectDataModel.cs:2099`, `part.Visibility =
    // entitySnapshot.Visibility`). On the IMPORT path it is never written, so
    // C#'s `bool` default is what `DynamoClient.cs:1179` marshals, and the row
    // carries an explicit `false` that means "nobody said". Measured across
    // this account: 6 946 of 6 946 part rows over 12 assets read `false`,
    // INCLUDING every asset's own ROOT, which no author would ever save.
    //
    // Unity does not trust it either — it applies the value and then repairs
    // the result in `AssetFactory.FixInvisibleParts` (C3D-8181): "if all parts
    // are invisible, it's probably an erroneous library asset", followed by
    // `SetActive(true)` on every part. Verbatim port. Deliberately NOT a
    // blanket `true`: an asset with SOME parts hidden is authored data, and
    // only the all-hidden case is provably the unwritten default.
    const allPartsInvisible = parts.every((part) => part.Visibility === false);

    // ⚠ The two roots take a pose Unity NEVER reads off the part row, and
    // copying it verbatim is what made a library placement land metres away
    // (user-reported: "it appears far above the table, vs Unity's placement at
    // the scene anchor" — the root row of the reported asset carries
    // `y: 14.55`, and rows elsewhere in this account reach `x: 4102`):
    //
    //  - The ENTITY root ("0,"). `ModifyAssetUsingPartDataAsync` guards its
    //    whole transform block with `if (entityPart != entity)`
    //    (`ProjectModel_LoadAsset.cs:159`), so the root keeps the pose
    //    `WrapAndShiftOrigin` gave it — the SCENE ANCHOR
    //    (`ProjectModelSlim.cs:2217`). Identity here, which is also exactly
    //    what the import path writes for a root snapshot.
    //  - The origin-shift ROOT ("0,1,", the wrapper Unity names `"Root"` at
    //    `ProjectModelSlim.cs:2206`). Upstream's own comment: its stored
    //    position "describes the centering of the *original* import and is
    //    invalid for a re-exported library file, so blindly restoring it makes
    //    library assets spawn far away (AI-1163)". Its stored ROTATION and
    //    SCALE must still be applied — the scale is a unit conversion (mm→m
    //    for CAD) the export does not bake into the vertices. Unity keeps the
    //    freshly computed centering; the web's renderer resets that wrapper to
    //    identity (`resetLocalTransform`), so the fresh centering IS zero.
    //
    // Identified by NAME rather than by `AssetData.ImporterVersion` so the
    // decision needs no extra fetch and cannot silently fall back: the wrapper
    // only exists for `>= 1.0.1`, and only Unity's wrapper is called `"Root"`.
    const K_ORIGIN_SHIFT_ROOT_ID = "0,1,";
    const K_ORIGIN_SHIFT_ROOT_NAME = "Root";
    const K_IDENTITY_POSITION = { x: 0, y: 0, z: 0 };
    const K_IDENTITY_ROTATION = { x: 0, y: 0, z: 0 };
    const K_IDENTITY_SCALE = { x: 1, y: 1, z: 1 };

    const newEntityId = randomUuid();
    const snapshots: EntitySnapshotDto[] = parts.map((part) => {
      const isRoot = (part.HierachyID ?? "") === "0,";
      // The part's vectors are NORMALISED to `{x, y, z}` maps before they are
      // copied onto the snapshot, never passed through as whatever shape the
      // part row happened to carry. A V2 `parts.json` row (desktop ≥ 4.9)
      // holds them as JsonUtility `NameFloatPair[]`, and a pair array reaching
      // the Dynamo writer was serialised as `{0,0,0}` on every axis — the
      // placement rendered correctly from memory and collapsed to a zero-scale
      // point on the next reload (2026-09-03, "library models sometimes don't
      // appear"). The V2 reader now folds vectors at its boundary and the
      // writer normalises as a belt; this is the braces, at the one site that
      // MINTS rows from part data.
      const storedPosition = toNameFloatMap(part.Position);
      const storedRotation = toNameFloatMap(part.Rotation);
      const storedScale = toNameFloatMap(part.Scale);

      const isOriginShiftRoot =
        (part.HierachyID ?? "") === K_ORIGIN_SHIFT_ROOT_ID
        && (part.Name ?? "") === K_ORIGIN_SHIFT_ROOT_NAME;
      // See the policy above: the entity root is fully identity, and the
      // origin-shift root keeps its stored rotation + scale but loses the
      // stale centering position.
      const position =
        isRoot || isOriginShiftRoot ? K_IDENTITY_POSITION : storedPosition;
      const rotation = isRoot ? K_IDENTITY_ROTATION : storedRotation;
      const scale = isRoot ? K_IDENTITY_SCALE : storedScale;
      return {
        EntityID: newEntityId,
        SnapshotID: randomUuid(),
        PartID: part.ID,
        Name: part.Name ?? "",
        DisplayName: part.DisplayName ?? part.Name ?? "",
        IsRoot: isRoot,
        Visibility: allPartsInvisible ? true : part.Visibility !== false,
        HasMergedChildren: part.Merged === true,
        Position: position,
        Rotation: rotation,
        // Unity's `ToPartData` defaults an ABSENT Scale to `Vector3.one`
        // (such rows exist). Persisting an empty map would write an explicit
        // {0,0,0} scale, collapsing the asset after a reload — so the default
        // applies to an EMPTY normalised map, not only to `undefined`.
        Scale: hasVec3Axes(scale) ? scale : { x: 1, y: 1, z: 1 },
        // Unity seeds the snapshot's reset pose from the library part pose.
        InitialPos: position,
        InitialRot: rotation,
        // This placement's private copies, NOT the library part's own ids —
        // remapped one-for-one so order and length are preserved (see
        // `remapMaterialIds` for why length is load-bearing).
        MaterialIDs: remapMaterialIds(partMaterialIds(part), materialClone.idMap),
        // Part name aliases come down with the placement, which is what makes
        // them reusable across projects. Seeded equal to the library's copy,
        // so a freshly placed model is by definition in sync and the drift
        // toast has nothing to say about it.
        Aliases: aliasesToDto(readAliases(part.Aliases)),
        // Root only — the watermark describes the PLACEMENT, and its root is
        // the row `ProjectModelLibrarySync` reads and stamps.
        ...(isRoot && librarySyncedVersion !== undefined
          ? { LibrarySyncedVersion: librarySyncedVersion }
          : {}),
      } as EntitySnapshotDto;
    });
    const settings: EntitySettingsDto = {
      EntityID: newEntityId,
      DisplayOrder: nextEntityDisplayOrder(this.getEntities(sceneId)),
    } as EntitySettingsDto;

    const [entity] = mapEntities({
      sceneId,
      snapshots,
      settings: [settings],
      partIdToAssetId: state.m_partIdToAssetId,
      partHierarchyById: state.m_partHierarchyById,
      partTransformById: state.m_partTransformById,
    });
    if (!entity) throw new Error("Place from library produced no entity data.");
    // The part rows named material rows; nothing has fetched them. Await
    // BEFORE the emit below — the renderer applies materials at the end of the
    // entity build, so hydrating afterwards would show the model in the
    // fallback flat colour until something else re-applied. One batch read,
    // and non-fatal by contract, so a material outage still places the model.
    await internals(this).ensureMaterialsLoadedAsync(
      snapshots.flatMap((s) => s.MaterialIDs ?? []),
      signal,
    );
    appendEntity(this, sceneId, entity);
    const assetWasLinked = (state.m_assetIdsByScene.get(sceneId) ?? []).includes(assetId);
    addAssetToScene(this, sceneId, assetId); // idempotent
    this.onEntityChangesApplied.emit({ sceneId });

    try {
      await context.storage.createEntitySnapshots(
        context.customerId, context.projectId, sceneId, snapshots, signal,
      );
      await context.storage.createEntitySettings(
        context.customerId, context.projectId, sceneId, settings, signal,
      );
      await context.storage.updateSceneAssetIDs(
        context.customerId,
        context.projectId,
        sceneId,
        state.m_assetIdsByScene.get(sceneId) ?? [assetId],
        signal,
      );
      await syncProjectAssetIds(this, signal);
    } catch (err) {
      removeEntity(this, sceneId, newEntityId);
      // Also unlink the asset when THIS call is what linked it — otherwise the
      // scene keeps a local assetId with no entity using it, and the next
      // successful `updateSceneAssetIDs` from any other edit persists that
      // phantom id (the import path's `rollbackImport` does the same).
      if (!assetWasLinked) removeAssetFromScene(this, sceneId, assetId);
      this.onEntityChangesApplied.emit({ sceneId });
      throw err;
    }

    publishCreateEntity(this, sceneId, assetId, newEntityId);
    recordDuplicateUndo(this, sceneId, assetId, newEntityId, snapshots, settings);

    if (Ioc.isRegistered<ISelectionModel>(SELECTION_MODEL)) {
      const rootSnapshotId = snapshots.find((s) => s.IsRoot === true)?.SnapshotID;
      if (rootSnapshotId) Ioc.resolve<ISelectionModel>(SELECTION_MODEL).set([rootSnapshotId]);
    }

    this.scheduleActiveSceneThumbnail();
    return { entityId: newEntityId };
  }

  /**
   * Receive-side reconcile for a peer's `create_entity` event. The publisher
   * only sends ids (Unity `SendCreateEntityEvent`), so re-fetch the asset's
   * parts + this entity's snapshots/settings from cloud and append it — the
   * GLB itself loads through {@link GltfAssetCache.getOrLoad} normally.
   */
  async applyCreateEntityFromCloud(
    this: ProjectModel,
    sceneId: string,
    assetId: string,
    entityId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (this.getEntityById(sceneId, entityId)) return; // idempotent (self-echo / dup)
    const context = storageContext(this);
    if (!context) return;
    const state = internals(this);

    if (!state.m_partsLoadedAssets.has(assetId)) {
      const cloudParts = await context.storage.getParts(context.customerId, assetId, signal);
      registerParts(this, assetId, cloudParts);
    }
    const [snaps, settings] = await Promise.all([
      context.storage.getEntitySnapshots(context.customerId, context.projectId, sceneId, undefined, signal),
      context.storage.getEntitySettings(context.customerId, context.projectId, sceneId, signal),
    ]);
    if (this.getEntityById(sceneId, entityId)) return; // may have arrived while awaiting
    const mySnaps = snaps.filter((s) => s.EntityID === entityId);
    if (mySnaps.length === 0) return;
    const [entity] = mapEntities({
      sceneId,
      snapshots: mySnaps,
      settings,
      partIdToAssetId: state.m_partIdToAssetId,
      partHierarchyById: state.m_partHierarchyById,
      partTransformById: state.m_partTransformById,
    });
    if (!entity) return;
    // A peer's placement reaches us as ids only, so its snapshots can name
    // material rows this client has never fetched — same gap, same fix.
    await internals(this).ensureMaterialsLoadedAsync(
      mySnaps.flatMap((s) => s.MaterialIDs ?? []),
      signal,
    );
    appendEntity(this, sceneId, entity);
    addAssetToScene(this, sceneId, assetId);
    this.onEntityChangesApplied.emit({ sceneId });
  }
}

function assetDtoOf(assetId: string, parsed: ParsedModel): AssetDto {
  return {
    ID: assetId,
    FileName: parsed.displayName,
    Format: parsed.format,
    ObjectKey: assetObjectKeyForFormat(assetId, parsed.format),
    Scale: parsed.assetScale,
    ImporterVersion: parsed.importerVersion,
    TriangleCount: parsed.triangleCount,
    FileSize: parsed.blob.size,
  };
}

// -- DTO builders -----------------------------------------------------------

function partDtoOf(part: ParsedPart, assetId: string): PartDto {
  return {
    ID: part.partId,
    Name: part.name,
    DisplayName: part.displayName,
    HierachyID: part.hierarchyId,
    AssetID: assetId,
    Position: part.position,
    Rotation: part.rotationEuler,
    Scale: part.scale,
  };
}

function snapshotDtoOf(part: ParsedPart, entityId: string): EntitySnapshotDto {
  return {
    EntityID: entityId,
    SnapshotID: part.snapshotId,
    PartID: part.partId,
    Name: part.name,
    DisplayName: part.displayName,
    IsRoot: part.isRoot,
    Visibility: true,
    Position: part.position,
    Rotation: part.rotationEuler,
    Scale: part.scale,
    InitialPos: part.position,
    InitialRot: part.rotationEuler,
    MaterialIDs: [],
  };
}

// -- In-memory state helpers ------------------------------------------------

interface UploadInternals {
  readonly m_entitiesByScene: Map<string, EntityData[]>;
  readonly m_assetIdsByScene: Map<string, ReadonlyArray<string>>;
  readonly m_partIdToAssetId: Map<string, string>;
  readonly m_partHierarchyById: Map<string, string>;
  readonly m_partTransformById: Map<string, EntityTransform>;
  readonly m_partAliasesById: Map<string, ReadonlyArray<PartAlias>>;
  readonly m_partsLoadedAssets: Set<string>;
  /** Material row cache — seeded directly with freshly cloned rows so the
   *  `ensureMaterialsLoadedAsync` below them is a no-op instead of a
   *  round-trip for rows this client just wrote. */
  readonly m_materialsById: Map<string, MaterialDto>;
  readonly m_assetVersionById: Map<string, number>;
  /** Single writer for the library-material catalog + its reverse index. */
  registerPartMaterialIds(partId: string, materialIds: ReadonlyArray<string>): void;
  /** See `ProjectModel.ensureMaterialsLoadedAsync` — mandatory before any
   *  path here emits `onEntityChangesApplied` for snapshots it synthesized. */
  ensureMaterialsLoadedAsync(
    materialIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void>;
}

function internals(model: ProjectModel): UploadInternals {
  return model as unknown as UploadInternals;
}

function storageContext(model: ProjectModel): {
  storage: IStorageModel;
  customerId: string;
  projectId: string;
} | null {
  return (
    model as unknown as {
      getStorageContext(): {
        storage: IStorageModel;
        customerId: string;
        projectId: string;
      } | null;
    }
  ).getStorageContext();
}

/**
 * Bake + upload the asset-library thumbnail for a freshly imported asset.
 * Port of `ProjectModel_UploadAsset.CreateAssetPreview` (render) plus the
 * `assetpreview/{assetID}` `PutFile` leg of
 * `ProjectDataModel.CreateEntityAsync` — Unity renders the imported hierarchy
 * on its own into a 512×512 target and stores the PNG under the asset id, so
 * the library has a thumbnail ready the moment the asset is saved to it
 * (`AssetVersion++`). Web-imported assets showed the placeholder icon until
 * this landed.
 *
 * The pixels come from the viewport's `IPreviewCaptureHost`; with no viewport
 * mounted (or an asset the render cache can't resolve) the upload is skipped
 * and the library simply falls back to its placeholder.
 *
 * **Deviation from Unity:** the whole leg is non-fatal. Unity renders inside a
 * try/catch but keeps the S3 upload inside the import transaction, where a
 * failure rolls the cloud rows back. The web's `rollbackImport` is in-memory
 * only, so failing the import over a missing thumbnail would leave the asset /
 * part / snapshot rows written with the local model rolled back — strictly
 * worse than a placeholder icon.
 */
async function uploadAssetPreview(
  model: ProjectModel,
  context: { storage: IStorageModel; customerId: string },
  assetId: string,
  signal?: AbortSignal,
): Promise<void> {
  const host = (model as unknown as { m_previewCaptureHost: IPreviewCaptureHost | null })
    .m_previewCaptureHost;
  if (!host) return;
  try {
    const bytes = await host.captureAssetPreviewAsync(assetId);
    if (!bytes || bytes.length === 0) return;
    await context.storage.putAssetPreviewObject(
      context.customerId,
      assetId,
      bytes,
      signal,
    );
  } catch (err) {
    console.warn(`[ProjectModel] asset preview upload failed (asset=${assetId}):`, err);
  }
}

function publishCreateEntity(
  model: ProjectModel,
  sceneId: string,
  assetId: string,
  entityId: string,
): void {
  // Unity `SendCreateEntityEvent(projectId, sceneId, assetId, entityId)` —
  // peers re-fetch parts + asset from cloud and reconcile.
  (
    model as unknown as {
      publishLiveProjectEvent(type: string, payload: Record<string, unknown>): void;
    }
  ).publishLiveProjectEvent("create_entity", {
    sceneId,
    SceneID: sceneId,
    SceneId: sceneId,
    assetId,
    AssetID: assetId,
    AssetId: assetId,
    entityId,
    EntityID: entityId,
    EntityId: entityId,
  });
}

/**
 * Record an undo entry for a completed import. `record` fires `redo` once on
 * push, so the first `redo` is a no-op (the model is already spawned). A later
 * undo deletes the entity + unlinks the asset; redo re-creates the entity rows
 * against the still-uploaded asset (Unity's `existingAssetInfo` fast path).
 */
function recordImportUndo(
  model: ProjectModel,
  sceneId: string,
  assetId: string,
  entityId: string,
  parts: ReadonlyArray<PartDto>,
  snapshots: ReadonlyArray<EntitySnapshotDto>,
  settings: EntitySettingsDto,
): void {
  if (!Ioc.isRegistered<IUndoRedo>(UNDO_REDO)) return;
  let firstRedo = true;
  Ioc.resolve<IUndoRedo>(UNDO_REDO).record({
    label: "Import model",
    redo: () => {
      if (firstRedo) {
        firstRedo = false;
        return; // initial spawn already happened in importModelAsync
      }
      void respawnImport(model, sceneId, assetId, entityId, parts, snapshots, settings);
    },
    undo: () => {
      void undoImport(model, sceneId, assetId);
    },
  });
}

async function undoImport(
  model: ProjectModel,
  sceneId: string,
  assetId: string,
): Promise<void> {
  const entity = findEntityByAsset(model, sceneId, assetId);
  if (entity) await model.deleteEntityAsync(sceneId, entity.id); // in-memory + cloud entity rows
  removeAssetFromScene(model, sceneId, assetId); // in-memory unlink
  const context = storageContext(model);
  if (context) {
    await context.storage.updateSceneAssetIDs(
      context.customerId,
      context.projectId,
      sceneId,
      internals(model).m_assetIdsByScene.get(sceneId) ?? [],
    );
    // Unlink shrinks the project-row membership too.
    await syncProjectAssetIds(model);
  }
}

async function respawnImport(
  model: ProjectModel,
  sceneId: string,
  assetId: string,
  entityId: string,
  parts: ReadonlyArray<PartDto>,
  snapshots: ReadonlyArray<EntitySnapshotDto>,
  settings: EntitySettingsDto,
): Promise<void> {
  if (model.getEntityById(sceneId, entityId)) return;
  registerParts(model, assetId, parts);
  await respawnEntityRows(model, sceneId, assetId, snapshots, settings);
}

/**
 * Shared redo leg for import + duplicate undo commands: re-append the entity
 * from its retained rows and re-persist them (the asset, part rows, and S3
 * object survive an entity-only undo — Unity's `existingAssetInfo` fast path).
 * Assumes the asset's parts are already registered in the catalog.
 */
async function respawnEntityRows(
  model: ProjectModel,
  sceneId: string,
  assetId: string,
  snapshots: ReadonlyArray<EntitySnapshotDto>,
  settings: EntitySettingsDto,
): Promise<void> {
  const state = internals(model);
  const [entity] = mapEntities({
    sceneId,
    snapshots,
    settings: [settings],
    partIdToAssetId: state.m_partIdToAssetId,
    partHierarchyById: state.m_partHierarchyById,
    partTransformById: state.m_partTransformById,
  });
  if (!entity) return;
  appendEntity(model, sceneId, entity);
  addAssetToScene(model, sceneId, assetId);
  model.onEntityChangesApplied.emit({ sceneId });
  const context = storageContext(model);
  if (context) {
    await context.storage.createEntitySnapshots(context.customerId, context.projectId, sceneId, snapshots);
    await context.storage.createEntitySettings(context.customerId, context.projectId, sceneId, settings);
    await context.storage.updateSceneAssetIDs(
      context.customerId,
      context.projectId,
      sceneId,
      state.m_assetIdsByScene.get(sceneId) ?? [assetId],
    );
    await syncProjectAssetIds(model);
    // Redo re-creates the rows — peers must re-reconcile just like the
    // original import (the matching undo publishes `delete_entity` via
    // `deleteEntityAsync`). Only meaningful once the rows exist in cloud.
    publishCreateEntity(model, sceneId, assetId, entityId(snapshots));
  }
}

/** Root entity id carried by a retained snapshot-row set. */
function entityId(snapshots: ReadonlyArray<EntitySnapshotDto>): string {
  for (const snap of snapshots) {
    if (typeof snap.EntityID === "string" && snap.EntityID) return snap.EntityID;
  }
  return "";
}

/**
 * Undo entry for a completed duplicate. Same guarded-first-`redo` pattern as
 * {@link recordImportUndo}, but undo only deletes the duplicated entity —
 * the asset stays linked to the scene because the source entity still uses it
 * (Unity `UndoSpawnedAsset` also just calls `DeleteEntityAsync`).
 */
function recordDuplicateUndo(
  model: ProjectModel,
  sceneId: string,
  assetId: string,
  entityId: string,
  snapshots: ReadonlyArray<EntitySnapshotDto>,
  settings: EntitySettingsDto,
): void {
  if (!Ioc.isRegistered<IUndoRedo>(UNDO_REDO)) return;
  let firstRedo = true;
  Ioc.resolve<IUndoRedo>(UNDO_REDO).record({
    label: "Duplicate",
    redo: () => {
      if (firstRedo) {
        firstRedo = false;
        return; // initial spawn already happened in duplicateEntityAsync
      }
      if (model.getEntityById(sceneId, entityId)) return;
      void respawnEntityRows(model, sceneId, assetId, snapshots, settings);
    },
    undo: () => {
      void model.deleteEntityAsync(sceneId, entityId);
    },
  });
}

function findEntityByAsset(
  model: ProjectModel,
  sceneId: string,
  assetId: string,
): EntityData | null {
  return model.getEntities(sceneId).find((e) => e.assetId === assetId) ?? null;
}

interface ProjectAssetCommitLog {
  s3Object: boolean;
  assetRow: boolean;
  partRows: boolean;
}

function emptyProjectAssetCommitLog(): ProjectAssetCommitLog {
  return {
    s3Object: false,
    assetRow: false,
    partRows: false,
  };
}

async function rollbackProjectAssetImportAsync(
  model: ProjectModel,
  context: { storage: IStorageModel; customerId: string; projectId: string },
  assetId: string,
  objectKey: string,
  parts: ReadonlyArray<PartDto>,
  committed: ProjectAssetCommitLog,
): Promise<void> {
  unregisterParts(model, assetId, parts);
  evictFromCache(assetId);

  const { storage, customerId } = context;
  const attempt = async (label: string, fn: () => Promise<unknown>): Promise<void> => {
    try {
      await fn();
    } catch (err) {
      console.warn(
        `[ProjectModel] console import rollback: failed to restore ${label} (asset=${assetId}):`,
        err,
      );
    }
  };

  if (committed.partRows) {
    const partIds = parts
      .map((part) => part.ID)
      .filter((id): id is string => typeof id === "string" && id.length > 0);
    await attempt("part rows", () => storage.deleteParts(customerId, assetId, partIds));
  }
  if (committed.assetRow) {
    await attempt("asset preview", () =>
      storage.deleteAssetPreviewObject(customerId, assetId),
    );
    await attempt("asset row", () => storage.deleteAsset(customerId, assetId));
  }
  if (committed.s3Object) {
    await attempt("asset object", () =>
      storage.deleteAssetObject(customerId, objectKey),
    );
  }
}

/**
 * Which of the import's cloud writes need compensation (in write order).
 *
 * Two flavours of marker: the single-item writes (`s3Object`, `assetRow`,
 * `settingsRow`) are atomic, so they are marked AFTER the await resolves —
 * a rejection means nothing landed. The batch writes (`partRows`,
 * `snapshotRows`) are chunked BatchWriteItem loops that can half-commit, so
 * they are marked BEFORE the await is issued: "started" already means "may
 * need sweeping", and the idempotent missing-key-tolerant deletes make an
 * unnecessary sweep free.
 */
interface ImportCommitLog {
  s3Object: boolean;
  assetRow: boolean;
  partRows: boolean;
  snapshotRows: boolean;
  settingsRow: boolean;
}

/** An error that means "the caller's signal aborted", however it was wrapped. */
function isAbortRejection(err: unknown, signal?: AbortSignal): boolean {
  if (signal?.aborted) return true;
  return (err instanceof DOMException || err instanceof Error) && err.name === "AbortError";
}

function emptyCommitLog(): ImportCommitLog {
  return {
    s3Object: false,
    assetRow: false,
    partRows: false,
    snapshotRows: false,
    settingsRow: false,
  };
}

/**
 * Undo a failed import: in-memory spawn + cache injection first (so the UI
 * reflects the failure immediately), then compensate every COMMITTED cloud
 * write in reverse order. The web analogue of Unity's `IRollbackStack`
 * (`ProjectDataModel.CreateEntityAsync`, `ProjectDataModel.cs:1699`) — and
 * broader than upstream, which only unwinds on `OperationCanceledException`.
 *
 * Rules the whole unwind follows (see the file header):
 * - NO abort signal — the caller's controller may already be aborted, and a
 *   cleanup that cancels itself leaves exactly the orphans it exists to
 *   remove.
 * - Each leg is individually guarded: a failed delete logs what it orphaned
 *   and keeps unwinding, and nothing thrown here can mask the original
 *   import error (the caller rethrows that one).
 */
async function rollbackImportAsync(
  model: ProjectModel,
  context: { storage: IStorageModel; customerId: string; projectId: string },
  args: {
    sceneId: string;
    entityId: string;
    assetId: string;
    objectKey: string;
    parts: ReadonlyArray<PartDto>;
    snapshots: ReadonlyArray<EntitySnapshotDto>;
    committed: ImportCommitLog;
  },
): Promise<void> {
  const { sceneId, entityId, assetId, objectKey, parts, snapshots, committed } = args;
  removeEntity(model, sceneId, entityId);
  removeAssetFromScene(model, sceneId, assetId);
  unregisterParts(model, assetId, parts);
  evictFromCache(assetId);
  model.onEntityChangesApplied.emit({ sceneId });

  const { storage, customerId, projectId } = context;
  const attempt = async (label: string, fn: () => Promise<void>): Promise<void> => {
    try {
      await fn();
    } catch (err) {
      console.warn(
        `[ProjectModel] import rollback: failed to delete ${label} (asset=${assetId}) — orphaned in cloud:`,
        err,
      );
    }
  };

  // Reverse write order, so the scene-row link unwinds first. Once the
  // sequence reached the `updateSceneAssetIDs` step (settingsRow committed),
  // that UpdateItem may have landed server-side even when the client saw a
  // rejection (lost response — the very failure mode its retry exists for),
  // leaving the scene row naming an asset whose every other row is about to
  // be deleted. `removeAssetFromScene` already pruned the in-memory list
  // above, so re-persisting it is an idempotent whole-list SET that either
  // repairs the phantom link or rewrites the value already there.
  if (committed.settingsRow) {
    await attempt("scene AssetIDs link", () =>
      storage.updateSceneAssetIDs(
        customerId,
        projectId,
        sceneId,
        (model as unknown as UploadInternals).m_assetIdsByScene.get(sceneId) ?? [],
      ),
    );
  }
  // `deleteEntity` removes the settings row AND every snapshot row in one
  // call (both share the entity's sk family), so the two newest steps
  // compensate together; Dynamo deletes are idempotent, so a half-committed
  // batch is fine to sweep.
  if (committed.snapshotRows || committed.settingsRow) {
    const snapshotIds = snapshots
      .map((s) => s.SnapshotID)
      .filter((id): id is string => typeof id === "string" && id.length > 0);
    await attempt("entity snapshot/settings rows", () =>
      storage.deleteEntity(customerId, projectId, sceneId, entityId, snapshotIds),
    );
  }
  if (committed.partRows) {
    const partIds = parts
      .map((p) => p.ID)
      .filter((id): id is string => typeof id === "string" && id.length > 0);
    await attempt("part rows", () => storage.deleteParts(customerId, assetId, partIds));
  }
  if (committed.assetRow) {
    // The preview upload is best-effort with no marker of its own; the
    // delete tolerates a missing key, so sweep it whenever the asset row
    // (its predecessor in the write order) committed.
    await attempt("asset preview object", () =>
      storage.deleteAssetPreviewObject(customerId, assetId),
    );
    await attempt("asset row", () => storage.deleteAsset(customerId, assetId));
  }
  if (committed.s3Object) {
    await attempt("asset object", () => storage.deleteAssetObject(customerId, objectKey));
  }
}

function registerParts(model: ProjectModel, assetId: string, parts: ReadonlyArray<PartDto>): void {
  const state = internals(model);
  for (const part of parts) {
    if (!part.ID) continue;
    state.m_partIdToAssetId.set(part.ID, assetId);
    if (part.HierachyID) state.m_partHierarchyById.set(part.ID, part.HierachyID);
    state.m_partTransformById.set(part.ID, {
      position: vec3Of(part.Position),
      rotation: vec3Of(part.Rotation),
      scale: vec3Of(part.Scale, [1, 1, 1]),
    });
    // Library alias mirror for the drift check (see `ProjectModelAliases.ts`).
    // Cleared entries are deleted so map membership stays truthful.
    const aliases = readAliases(part.Aliases);
    if (aliases.length > 0) state.m_partAliasesById.set(part.ID, aliases);
    else state.m_partAliasesById.delete(part.ID);
    // Arms the copy-on-write guard. `m_partsLoadedAssets.add` below makes the
    // scene-load `getParts` leg skip this asset for the rest of the session,
    // so if we do not record its library materials HERE nothing ever will —
    // and an edit on a pre-fix placement of it would reach the library row.
    state.registerPartMaterialIds(part.ID, partMaterialIds(part));
  }
  state.m_partsLoadedAssets.add(assetId);
}

/**
 * Splice an entity into the scene list at its `DisplayOrder` seat rather than
 * blindly at the end. `getEntities` order IS the part-path root order, so an
 * in-session insert has to land where the next reload's `mapEntities` sort puts
 * it — after every entity with a lower-or-equal order, which also reproduces
 * the stable-sort tie handling. Locally minted orders are always the new max,
 * so this only moves rows for a peer's `create_entity` (a Unity client can send
 * any order, e.g. re-adding one that filled a gap).
 */
function appendEntity(model: ProjectModel, sceneId: string, entity: EntityData): void {
  const state = internals(model);
  const list = state.m_entitiesByScene.get(sceneId) ?? [];
  const at = list.findIndex((e) => e.displayOrder > entity.displayOrder);
  const next = [...list];
  next.splice(at === -1 ? next.length : at, 0, entity);
  state.m_entitiesByScene.set(sceneId, next);
}

function removeEntity(model: ProjectModel, sceneId: string, entityId: string): void {
  const state = internals(model);
  const list = state.m_entitiesByScene.get(sceneId);
  if (!list) return;
  state.m_entitiesByScene.set(
    sceneId,
    list.filter((e) => e.id !== entityId),
  );
}

/**
 * Keep the PROJECT-row `AssetIDs` in sync (Unity
 * `CustomerModel.UpdateCurrentProjectAssetIDs` → `DynamoClient` :286).
 *
 * The web persists scene-level `AssetIDs` for rendering, but the ASSET
 * LIBRARY (both clients) decides "is this asset in use?" from the project
 * row. Without this the library sees every web-created reference as an
 * orphan, which is exactly the fail-safe that currently disables Delete.
 * Recomputed as the union across the project's scenes so an unlink shrinks
 * it too. Failures are logged, never thrown — a stale membership list must
 * not break the edit that triggered it.
 */
async function syncProjectAssetIds(
  model: ProjectModel,
  signal?: AbortSignal,
): Promise<void> {
  try {
    await model.syncProjectAssetIdsAsync(signal);
  } catch (err) {
    console.warn("[ProjectModel] project AssetIDs sync failed:", err);
  }
}

function addAssetToScene(model: ProjectModel, sceneId: string, assetId: string): void {
  const state = internals(model);
  const existing = state.m_assetIdsByScene.get(sceneId) ?? [];
  if (!existing.includes(assetId)) {
    state.m_assetIdsByScene.set(sceneId, [...existing, assetId]);
  }
  const scene = model.getScene(sceneId);
  if (scene && !scene.assetIds.includes(assetId)) {
    model.applySceneChanges({ ...scene, assetIds: [...scene.assetIds, assetId] });
  }
}

function removeAssetFromScene(model: ProjectModel, sceneId: string, assetId: string): void {
  const state = internals(model);
  const existing = state.m_assetIdsByScene.get(sceneId) ?? [];
  state.m_assetIdsByScene.set(
    sceneId,
    existing.filter((id) => id !== assetId),
  );
  const scene = model.getScene(sceneId);
  if (scene && scene.assetIds.includes(assetId)) {
    model.applySceneChanges({
      ...scene,
      assetIds: scene.assetIds.filter((id) => id !== assetId),
    });
  }
}

function unregisterParts(
  model: ProjectModel,
  assetId: string,
  parts: ReadonlyArray<PartDto>,
): void {
  const state = internals(model);
  for (const part of parts) {
    if (!part.ID) continue;
    state.m_partIdToAssetId.delete(part.ID);
    state.m_partHierarchyById.delete(part.ID);
    state.m_partTransformById.delete(part.ID);
  }
  state.m_partsLoadedAssets.delete(assetId);
}

function evictFromCache(assetId: string): void {
  if (!Ioc.isRegistered<GltfAssetCache>(GLTF_ASSET_CACHE)) return;
  Ioc.resolve<GltfAssetCache>(GLTF_ASSET_CACHE).evict(assetId);
}

function injectIntoCache(assetId: string, parsed: ParsedModel): void {
  if (!Ioc.isRegistered<GltfAssetCache>(GLTF_ASSET_CACHE)) return;
  const cache = Ioc.resolve<GltfAssetCache>(GLTF_ASSET_CACHE);
  cache.injectParsedGltf(
    assetId,
    parsed.gltf,
    parsed.assetScale,
    parseImporterVersion(parsed.importerVersion),
  );
}

/** True when the map carries every axis a Dynamo `M` vector needs. */
function hasVec3Axes(map: NameFloatMap): boolean {
  return typeof map.x === "number" && typeof map.y === "number" && typeof map.z === "number";
}

function vec3Of(
  value: unknown,
  fallback: readonly [number, number, number] = [0, 0, 0],
): EntityTransform["position"] {
  // `toNameFloatMap`, not a cast: a V2 part row carries its vectors as
  // `NameFloatPair[]`, which a cast reads as `{}` and silently defaults.
  const map = toNameFloatMap(value);
  const num = (v: unknown, d: number) => (typeof v === "number" && Number.isFinite(v) ? v : d);
  return [num(map.x, fallback[0]), num(map.y, fallback[1]), num(map.z, fallback[2])];
}

function parseImporterVersion(version: string): ReadonlyArray<number> {
  const parts = version.split(".").map((p) => Number.parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return [0, 0, 0];
  while (parts.length < 3) parts.push(0);
  return parts.slice(0, 3);
}
